/**
 * FERP Document Trail Panel (DRR-079)
 *
 * Right-panel tab showing document-to-workflow traceability:
 * workflow context, approval chain, regulatory compliance,
 * version history, and document lifecycle timeline.
 *
 * Reads from PdfSuiteContext.state.documentTrailMeta.
 * All data comes from backend — Dumb Frontend compliant.
 */

import {
  Stack,
  Text,
  Badge,
  Group,
  Timeline,
  Accordion,
  Paper,
  Center,
  ThemeIcon,
} from '@mantine/core';
import {
  IconRoute,
  IconGitBranch,
  IconShieldCheck,
  IconHistory,
  IconTimeline,
  IconChecklist,
  IconUserCheck,
  IconScale,
  IconClock,
  IconFileDescription,
} from '@tabler/icons-react';

import { usePdfSuite } from './PdfSuiteContext';
import type { DocumentTrailMeta } from './types';

/** Format ISO date to locale short date */
function fmtDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

/** Format ISO date to locale date + time */
function fmtDateTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function TrailField({ label, value }: { label: string; value: string | null | undefined }) {
  if (!value) return null;
  return (
    <Group gap="xs" wrap="nowrap">
      <Text size="xs" c="dimmed" style={{ minWidth: 80 }}>
        {label}
      </Text>
      <Text size="xs" fw={500} lineClamp={1}>
        {value}
      </Text>
    </Group>
  );
}

function IdBadgeList({ ids, color = 'blue' }: { ids: string[]; color?: string }) {
  if (ids.length === 0) return null;
  return (
    <Group gap={4} wrap="wrap">
      {ids.map((id) => (
        <Badge key={id} size="xs" variant="light" color={color}>
          {id}
        </Badge>
      ))}
    </Group>
  );
}

function WorkflowSection({ meta }: { meta: DocumentTrailMeta }) {
  const hasWorkflow = meta.workflowInstanceId || meta.taskId;
  if (!hasWorkflow) return null;

  return (
    <Accordion.Item value="workflow">
      <Accordion.Control icon={<IconGitBranch size={16} />}>
        <Text size="sm" fw={600}>Workflow Context</Text>
      </Accordion.Control>
      <Accordion.Panel>
        <Stack gap="xs">
          <TrailField label="Workflow" value={meta.workflowInstanceId} />
          <TrailField label="Task" value={meta.taskId} />
        </Stack>
      </Accordion.Panel>
    </Accordion.Item>
  );
}

function ApprovalSection({ meta }: { meta: DocumentTrailMeta }) {
  const hasApproval =
    meta.approvalWorkflowId ||
    meta.approverActorIds.length > 0 ||
    meta.requiredApproverRoleIds.length > 0;
  if (!hasApproval) return null;

  return (
    <Accordion.Item value="approval">
      <Accordion.Control icon={<IconUserCheck size={16} />}>
        <Group gap="xs">
          <Text size="sm" fw={600}>Approval Chain</Text>
          {meta.approverActorIds.length > 0 && (
            <Badge size="xs" variant="light" color="violet">
              {meta.approverActorIds.length}
            </Badge>
          )}
        </Group>
      </Accordion.Control>
      <Accordion.Panel>
        <Stack gap="xs">
          <TrailField label="Workflow" value={meta.approvalWorkflowId} />
          {meta.approverActorIds.length > 0 && (
            <div>
              <Text size="xs" c="dimmed" mb={4}>Approvers</Text>
              <IdBadgeList ids={meta.approverActorIds} color="violet" />
            </div>
          )}
          {meta.requiredApproverRoleIds.length > 0 && (
            <div>
              <Text size="xs" c="dimmed" mb={4}>Required Roles</Text>
              <IdBadgeList ids={meta.requiredApproverRoleIds} color="grape" />
            </div>
          )}
        </Stack>
      </Accordion.Panel>
    </Accordion.Item>
  );
}

function RegulatorySection({ meta }: { meta: DocumentTrailMeta }) {
  const hasRegulatory =
    meta.regulatoryRuleIds.length > 0 ||
    meta.regulatoryEvidenceName ||
    meta.regulatoryDeadline ||
    meta.complianceFrameworks.length > 0 ||
    meta.policyId ||
    meta.validationId;
  if (!hasRegulatory) return null;

  return (
    <Accordion.Item value="regulatory">
      <Accordion.Control icon={<IconShieldCheck size={16} />}>
        <Group gap="xs">
          <Text size="sm" fw={600}>Regulatory Compliance</Text>
          {meta.regulatoryRuleIds.length > 0 && (
            <Badge size="xs" variant="light" color="teal">
              {meta.regulatoryRuleIds.length} rules
            </Badge>
          )}
        </Group>
      </Accordion.Control>
      <Accordion.Panel>
        <Stack gap="xs">
          {meta.regulatoryRuleIds.length > 0 && (
            <div>
              <Text size="xs" c="dimmed" mb={4}>Rules</Text>
              <IdBadgeList ids={meta.regulatoryRuleIds} color="teal" />
            </div>
          )}
          <TrailField label="Evidence" value={meta.regulatoryEvidenceName} />
          {meta.regulatoryDeadline && (
            <Group gap="xs" wrap="nowrap">
              <Text size="xs" c="dimmed" style={{ minWidth: 80 }}>Deadline</Text>
              <Badge size="xs" variant="light" color="orange" leftSection={<IconClock size={10} />}>
                {fmtDate(meta.regulatoryDeadline)}
              </Badge>
            </Group>
          )}
          {meta.complianceFrameworks.length > 0 && (
            <div>
              <Text size="xs" c="dimmed" mb={4}>Frameworks</Text>
              <IdBadgeList ids={meta.complianceFrameworks} color="cyan" />
            </div>
          )}
          <TrailField label="Policy" value={meta.policyId} />
          <TrailField label="Validation" value={meta.validationId} />
        </Stack>
      </Accordion.Panel>
    </Accordion.Item>
  );
}

function VersionSection({ meta }: { meta: DocumentTrailMeta }) {
  return (
    <Accordion.Item value="version">
      <Accordion.Control icon={<IconHistory size={16} />}>
        <Group gap="xs">
          <Text size="sm" fw={600}>Version History</Text>
          <Badge size="xs" variant="light">v{meta.version}</Badge>
        </Group>
      </Accordion.Control>
      <Accordion.Panel>
        <Stack gap="xs">
          <TrailField label="Version" value={`v${meta.version}`} />
          <TrailField label="Created" value={fmtDateTime(meta.versionCreatedAt)} />
          <TrailField label="By" value={meta.versionCreatedBy} />
          {meta.versionChangeDescription && (
            <div>
              <Text size="xs" c="dimmed" mb={2}>Change Description</Text>
              <Paper withBorder p="xs">
                <Text size="xs">{meta.versionChangeDescription}</Text>
              </Paper>
            </div>
          )}
          <TrailField label="Previous" value={meta.previousVersionId} />
          {meta.versionHistoryIds.length > 0 && (
            <div>
              <Text size="xs" c="dimmed" mb={4}>
                History ({meta.versionHistoryIds.length} versions)
              </Text>
              <IdBadgeList ids={meta.versionHistoryIds} color="gray" />
            </div>
          )}
        </Stack>
      </Accordion.Panel>
    </Accordion.Item>
  );
}

function LifecycleSection({ meta }: { meta: DocumentTrailMeta }) {
  return (
    <Accordion.Item value="lifecycle">
      <Accordion.Control icon={<IconTimeline size={16} />}>
        <Text size="sm" fw={600}>Document Lifecycle</Text>
      </Accordion.Control>
      <Accordion.Panel>
        <Timeline active={2} bulletSize={20} lineWidth={2}>
          <Timeline.Item
            bullet={<IconFileDescription size={12} />}
            title={<Text size="xs" fw={600}>Created</Text>}
          >
            <Text size="xs" c="dimmed">{fmtDateTime(meta.createdAt)}</Text>
            <Text size="xs" c="dimmed">by {meta.createdBy}</Text>
          </Timeline.Item>

          <Timeline.Item
            bullet={<IconChecklist size={12} />}
            title={
              <Group gap={4}>
                <Text size="xs" fw={600}>Status</Text>
                <Badge size="xs" variant="light">{meta.currentStatus}</Badge>
              </Group>
            }
          >
            <Text size="xs" c="dimmed">{fmtDateTime(meta.statusUpdatedAt)}</Text>
            <Text size="xs" c="dimmed">by {meta.statusUpdatedBy}</Text>
          </Timeline.Item>

          <Timeline.Item
            bullet={<IconScale size={12} />}
            title={<Text size="xs" fw={600}>Last Updated</Text>}
          >
            <Text size="xs" c="dimmed">{fmtDateTime(meta.updatedAt)}</Text>
            <Text size="xs" c="dimmed">by {meta.updatedBy}</Text>
          </Timeline.Item>
        </Timeline>

        {meta.sourceChaosDocumentId && (
          <Group gap="xs" mt="sm">
            <Text size="xs" c="dimmed">Source (Chaos→DSL):</Text>
            <Badge size="xs" variant="light" color="indigo">
              {meta.sourceChaosDocumentId}
            </Badge>
          </Group>
        )}
      </Accordion.Panel>
    </Accordion.Item>
  );
}

export function DocumentTrailPanel() {
  const { state } = usePdfSuite();
  const meta = state.documentTrailMeta;

  if (!meta) {
    return (
      <Center py="xl" px="md">
        <Stack align="center" gap="xs">
          <ThemeIcon size="lg" variant="light" color="gray">
            <IconRoute size={20} />
          </ThemeIcon>
          <Text size="sm" c="dimmed" ta="center">
            No trail data available.
          </Text>
          <Text size="xs" c="dimmed" ta="center">
            Open a document to view its workflow, approval, and compliance trail.
          </Text>
        </Stack>
      </Center>
    );
  }

  // Determine which sections are open by default (ones with data)
  const defaultOpen: string[] = [];
  if (meta.workflowInstanceId || meta.taskId) defaultOpen.push('workflow');
  if (meta.approvalWorkflowId || meta.approverActorIds.length > 0) defaultOpen.push('approval');
  if (meta.regulatoryRuleIds.length > 0 || meta.complianceFrameworks.length > 0) defaultOpen.push('regulatory');
  defaultOpen.push('version', 'lifecycle');

  return (
    <Stack gap={0} p="xs">
      <Group gap="xs" mb="xs">
        <IconRoute size={14} />
        <Text size="xs" fw={600} c="dimmed">DOCUMENT TRAIL</Text>
      </Group>

      <Accordion multiple defaultValue={defaultOpen} variant="separated">
        <WorkflowSection meta={meta} />
        <ApprovalSection meta={meta} />
        <RegulatorySection meta={meta} />
        <VersionSection meta={meta} />
        <LifecycleSection meta={meta} />
      </Accordion>
    </Stack>
  );
}
